import apiConfig from "../ApiConfig";

// Fetch enabled default modules
export const fetchEnabledModulesApi = async () => {
  try {
    const customerId = localStorage.getItem("user_id");
    if (!customerId) return [];

    const response = await apiConfig.get(
      `/api/customer-requirements/${customerId}/enabled/`
    );

    return response.data.enabled_modules || [];
  } catch (error) {
    console.error("Error fetching enabled modules:", error);
    throw error;
  }
};

// Fetch top performing customers
export const fetchTopCustomersApi = async () => {
  try {
    const response = await apiConfig.get(`/api/top-performing-customers/`);
    return response.data.top_customers || [];
  } catch (error) {
    console.error("Error fetching top customers:", error);
    throw error;
  }
};

// Fetch company modules
export const fetchEnabledModulesForCompanyApi = async (userId) => {
  try {
    const response = await apiConfig.get(
      `/api/customer-enabled-modules/${userId}/`
    );

    return response.data.enabled_modules || [];
  } catch (error) {
    console.error("Error fetching company modules:", error);
    throw error;
  }
};

// Fetch CRM categories
export const fetchCategoriesApi = async () => {
  try {
    const response = await apiConfig.get(`/api/ai-crm/api/v1/categories/`);

    const data = response.data;
    return Array.isArray(data)
      ? data
      : data.results
      ? data.results
      : [];
  } catch (error) {
    console.error("Error fetching categories:", error);
    return [];
  }
};

// Fetch subcategory + TrustCenter toggles
export const fetchSubCategoriesWithTrustCenterApi = async (
  categoryId,
  customerId
) => {
  try {
    const catRes = await apiConfig.get(
      `/api/ai-crm/api/v1/categories/${categoryId}/sub_categories/`
    );

    const subcategories = Array.isArray(catRes.data)
      ? catRes.data
      : catRes.data.sub_categories || [];

    const trustRes = await apiConfig.get(
      `/api/ai-crm/api/v1/customer-requirements/${customerId}/?type=TrustCenter`
    );

    const trustCenterSaved = trustRes.data?.data || {};

    const toggles = {};
    subcategories.forEach((sub) => {
      toggles[sub.id] = trustCenterSaved[sub.id] || false;
    });

    return { subcategories, toggles };
  } catch (error) {
    console.error("Error loading TrustCenter subcategories:", error);
    return { subcategories: [], toggles: {} };
  }
};

// Update TrustCenter toggle
export const updateTrustCenterToggleApi = async (
  customerId,
  subId,
  value
) => {
  try {
    console.log(`🔄 [UPDATE] TrustCenter Toggle - Customer: ${customerId}, SubCategory: ${subId}, Value: ${value}`);
    
    const response = await apiConfig.put(
      `/api/ai-crm/api/v1/customer-requirements/update_requirement/`,
      {
        customer_id: customerId,
        type: "TrustCenter",
        field: subId,
        value,
      }
    );

    console.log(`✅ [SUCCESS] TrustCenter Toggle Updated - Customer: ${customerId}, SubCategory: ${subId}`);
    return response.data;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to update TrustCenter toggle - Customer: ${customerId}, SubCategory: ${subId}:`, error);
    throw error;
  }
};

export const updateAIAssetManagementToggleApi = async (
  customerId,
  connectorId,
  value
) => {
  try {
    console.log(`🔄 [UPDATE] AI Asset Management Toggle - Customer: ${customerId}, Connector: ${connectorId}, Value: ${value}`);
    
    const response = await apiConfig.put(
      `/api/ai-crm/api/v1/customer-requirements/update_ai_asset_manager/`,
      {
        customer_id: customerId,
        type: "AIAssetManagement",
        field: connectorId,
        value,
      }
    );

    console.log(`✅ [SUCCESS] AI Asset Management Toggle Updated - Customer: ${customerId}, Connector: ${connectorId}`);
    return response.data;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to update AI Asset Management toggle - Customer: ${customerId}, Connector: ${connectorId}:`, error);
    throw error;
  }
};

// Fetch customer requirements (Default, TrustCenter, AdditionalModule)
export const fetchCustomerRequirementsApi = async (
  customerId,
  tabName
) => {
  try {
    const response = await apiConfig.get(
      `/api/ai-crm/api/v1/customer-requirements/${customerId}/?type=${tabName}`
    );

    return response.data?.data || {};
  } catch (error) {
    console.error("Error fetching customer requirements:", error);
    throw error;
  }
};   

// General update for CRM customer requirement
export const updateCustomerRequirementApi = async (
  customerId,
  type,
  field,
  value
) => {
  try {
    console.log(`🔄 [UPDATE] Customer Requirement - Customer: ${customerId}, Type: ${type}, Field: ${field}, Value: ${value}`);
    
    const response = await apiConfig.put(
      `/api/customer-requirements/${customerId}/update/`,
      {
        type,
        field,
        value,
      }
    );
    
    console.log(`✅ [SUCCESS] Customer Requirement Updated - Customer: ${customerId}, Type: ${type}, Field: ${field}`);
    return response.data;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to update customer requirement - Customer: ${customerId}, Type: ${type}, Field: ${field}:`, error);
    throw error;
  }
};

// Update additional modules (AI DSPM, AI CSPM, etc.)
export const updateAdditionalModuleApi = async (
  customerId,
  moduleName,
  value
) => {
  try {
    console.log(`🔄 [UPDATE] Additional Module - Customer: ${customerId}, Module: ${moduleName}, Value: ${value}`);
    
    const response = await apiConfig.put(
      `/api/ai-crm/api/v1/customer-requirements/update_requirement/`,
      {
        customer_id: customerId,
        type: "AdditionalModule",
        field: moduleName,
        value,
      }
    );
    
    console.log(`✅ [SUCCESS] Additional Module Updated - Customer: ${customerId}, Module: ${moduleName}`);
    return response.data;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to update additional module - Customer: ${customerId}, Module: ${moduleName}:`, error);
    throw error;
  }
};

// Sidebar: enabled modules list
export const fetchEnabledModulesForSidebarApi = async (customerId) => {
  try {
    const types = ["Default", "AdditionalModule"];
    let enabled = [];

    for (const type of types) {
      const response = await apiConfig.get(
        `/api/ai-crm/api/v1/customer-requirements/${customerId}/?type=${type}`
      );

      const enabledList = Object.keys(response.data?.data || {}).filter(
        (m) => response.data.data[m] === true
      );

      enabled = [...enabled, ...enabledList];
    }

    return [...new Set(enabled)];
  } catch (error) {
    console.error("Error fetching sidebar modules:", error);
    throw error;
  }
};

// CRM Dashboard stats
export const fetchDashboardStatsApi = async () => {
  try {
    const response = await apiConfig.get(
      `/api/ai-crm/api/v1/dashboard/stats/`
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching dashboard stats:", error);
    throw error;
  }
};

// Fetch CRM customers list (uses apiCall wrapper from your component)
export const fetchCustomersApi = async (limit = 20, offset = 0) => {
  try {
    const response = await apiConfig.get(
      `/api/ai-crm/api/v1/list-customer-admins/?limit=${limit}&offset=${offset}`
    );
    return response.data;
  } catch (error) {
    console.error("Failed to load customers:", error);
    throw error;
  }
};
// export const fetchCustomersApi = async (apiCall, queryString) => {
//   try {
//     const response = await apiCall(`/api/ai-crm/api/v1/list-customer-admins/?${queryString}`);
//     return response.customers || [];
//   } catch (error) {
//     console.error("Failed to load customers:", error);
//     throw error;
//   }
// };

// Available modules list
export const fetchAvailableModulesApi = async (apiCall) => {
  try {
    const response = await apiCall("/modules");
    return response.modules || {};
  } catch (error) {
    console.error("Failed to load available modules:", error);
    throw error;
  }
};

// Audit log
export const fetchAuditLogApi = async (apiCall, customerId) => {
  try {
    const response = await apiCall(`/customers/${customerId}/audit-log`);
    return response.audit_log || [];
  } catch (error) {
    console.error("Failed to load audit log:", error);
    throw error;
  }
};

// Create customer
export const createCustomerApi = async (apiCall, customerData) => {
  try {
    console.log(`🆕 [CREATE] New Customer - Company: ${customerData.company_name}, Industry: ${customerData.industry}`);
    console.log('📋 [CREATE] Customer Data:', JSON.stringify(customerData, null, 2));
    
    const result = await apiCall("/api/ai-crm/api/v1/leads/", {
      method: "POST",
      body: JSON.stringify(customerData),
    });
    
    console.log(`✅ [SUCCESS] Customer Created - Company: ${customerData.company_name}, ID: ${result.customer_id || result.id}`);
    return result;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to create customer - Company: ${customerData.company_name}:`, error);
    throw error;
  }
};

// Update customer
export const updateCustomerApi = async (apiCall, customerId, customerData) => {
  try {
    console.log(`🔄 [UPDATE] Customer - ID: ${customerId}, Company: ${customerData.company_name || 'N/A'}`);
    console.log('📋 [UPDATE] Customer Data:', JSON.stringify(customerData, null, 2));
    
    const result = await apiCall(`/leads/${customerId}`, {
      method: "PUT",
      body: JSON.stringify(customerData),
    });
    
    console.log(`✅ [SUCCESS] Customer Updated - ID: ${customerId}`);
    return result;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to update customer - ID: ${customerId}:`, error);
    throw error;
  }
};

// Update customer status
export const updateCustomerStatusApi = async (
  apiCall,
  customerId,
  newStatus,
  reason
) => {
  try {
    console.log(`🔄 [UPDATE] Customer Status - ID: ${customerId}, New Status: ${newStatus}, Reason: ${reason || 'N/A'}`);
    
    const result = await apiCall(`/leads/${customerId}/status`, {
      method: "PUT",
      body: JSON.stringify({
        new_status: newStatus,
        reason,
        notify_customer: true,
      }),
    });
    
    console.log(`✅ [SUCCESS] Customer Status Updated - ID: ${customerId}, Status: ${newStatus}`);
    return result;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to update customer status - ID: ${customerId}, Status: ${newStatus}:`, error);
    throw error;
  }
};

// Fetch billing data
export const fetchBillingDataApi = async (apiCall) => {
  try {
    const response = await apiConfig.get(`/api/ai-crm/api/v1/billing/`);
    return response.data;
  } catch (error) {
    console.error("Error fetching billing data:", error);
    throw error;
  }
};

// Renew customer subscription
export const renewCustomerSubscriptionApi = async (userId, renewalData) => {
  try {
    console.log(`🔄 [RENEWAL] Customer Subscription - User ID: ${userId}`);
    console.log('📋 [RENEWAL] Renewal Data:', JSON.stringify(renewalData, null, 2));
    
    const response = await apiConfig.put(
      `/api/admin-user/user/${userId}/`,
      renewalData
    );
    
    console.log(`✅ [SUCCESS] Subscription Renewed - User ID: ${userId}, Employee Count: ${renewalData.employeecount}, Price: $${renewalData.price}, Renewal Date: ${renewalData.renewal_date}`);
    return response.data;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to renew subscription - User ID: ${userId}:`, error);
    throw error;
  }
};

// Get customer renewal history
export const getCustomerRenewalHistoryApi = async (customerId) => {
  try {
    console.log(`📋 [RENEWAL HISTORY] Fetching renewal history for customer: ${customerId}`);
    
    const response = await apiConfig.get(
      `/api/ai-crm/customer/${customerId}/renewal-history/`
    );
    
    console.log(`✅ [SUCCESS] Retrieved ${response.data.total_renewals} renewal records for customer ${customerId}`);
    return response.data;
  } catch (error) {
    console.error(`❌ [ERROR] Failed to fetch renewal history for customer ${customerId}:`, error);
    throw error;
  }
};

// ===============================
// Module Usage & Analytics APIs
// ===============================

// Super Admin – top modules across all customers
export const fetchSuperAdminModulesApi = async () => {
  try {
    const response = await apiConfig.get(`/api/customer-module-usage/`);
    return response.data || {};
  } catch (error) {
    console.error("Error fetching super admin modules:", error);
    throw error;
  }
};

// Company-specific enabled modules
export const fetchCompanyModuleUsageApi = async (userId) => {
  try {
    const response = await apiConfig.get(
      `/api/customer-module-usage/${userId}/`
    );
    return response.data || {};
  } catch (error) {
    console.error("Error fetching company module usage:", error);
    throw error;
  }
};

// Platform usage statistics
export const fetchUsageStatisticsApi = async () => {
  try {
    const response = await apiConfig.get(`/api/usage-statistics/`);
    return response.data || {};
  } catch (error) {
    console.error("Error fetching usage statistics:", error);
    throw error;
  }
};
